import { useEffect, useRef, useState } from "react";
import { isPlaced, type ComposedBlock, type DayPanelEntry } from "@jackedlogs/shared";

import { entryIdentity, findEntryIndex } from "./entryIdentity";
import type { Composer } from "./useComposer";

// The live staging → commit state machine shared by the edit-in-place
// composers (single Plan + program). The interaction is LIVE (logger
// model): there is no per-exercise Save — editing an exercise stages it in
// the composer form, which is committed to the in-memory draft the moment
// you collapse, switch exercise, ＋Add, or Save. One Save persists the
// whole draft. The glue here is the bug-prone part (deferred open after a
// re-derive, multi-add split, save-after-commit) — extracted ONCE so both
// modes share the same proven logic; the layout + editor chrome differ.
//
// `day` is the panel day the entries belong to (0 for single Plan,
// selectedDay for program); startEditEntry loads against it.
export function useEditInPlace({
  composer,
  entries,
  day = 0,
  addDays,
}: {
  composer: Composer;
  entries: DayPanelEntry[];
  day?: number;
  // Program only: which day(s) a freshly-added exercise lands on (the
  // selected day). Single mode omits it (one day → the allOf default).
  addDays?: Set<number>;
}) {
  const { commit, startEditEntry, addExercises, addSuperset, replaceMember } =
    composer;
  const { removeEntry, resetForm, save, form, formConflict, clearRamp, startClone } =
    composer;
  const { canCommit, togglePlacement } = composer;

  // The host picker serves two gestures: 'add' appends a fresh exercise,
  // {replace} swaps a member's exercise keeping its sets.
  const [picker, setPicker] = useState<"add" | { replace: number } | null>(
    null,
  );
  // Which entry is expanded (narrow) / selected (wide). Valid only while an
  // existing entry is in the form; a brand-new compose has no entry yet.
  const [editingIdx, setEditingIdx] = useState<number | null>(null);

  const hasForm = form.editing !== null || form.members.length > 0;
  const composingNew = form.editing === null && form.members.length > 0;

  // flush() persists whatever editor is open into the draft. commit() is a
  // no-op on an empty form, so it's always safe to call before a transition.
  // commit() runs setDraft+setForm asynchronously, so anything that must read
  // the COMMITTED draft (opening the next entry, saving) waits for the next
  // render rather than running synchronously off a stale snapshot.
  const flush = () => {
    if (hasForm) commit();
  };
  const collapse = () => {
    flush();
    // Exit ramp mode so the post-commit reload effect doesn't reopen the
    // editor we're closing (no-op for single — ramp is never set there).
    clearRamp();
    setEditingIdx(null);
  };

  // Deferred open: after flush() commits the previous editor, `blocks` (and
  // thus `entries`) re-derive into fresh objects. startEditEntry must load one
  // of THOSE — loading the pre-commit block would leave form.editing pointing
  // at an object no longer === any current block, and the commit duplicate-
  // guard (which skips self by identity) would then read the entry as a
  // conflict against itself and silently drop every later edit.
  //
  // ⛔ We record WHAT to reopen, not WHERE it sat. The index is not a stable
  // address (entryIdentity.ts says why), and PE-22 F made that reachable in one
  // gesture: unplacing the open exercise's last active week on this day drops
  // it from `entries` and slides everything after it up one. Missing → close
  // the editor; opening whatever slid into the slot would be silent and wrong.
  const [pendingOpen, setPendingOpen] = useState<string | null>(null);
  useEffect(() => {
    if (pendingOpen === null) return;
    setPendingOpen(null);
    const idx = findEntryIndex(entries, pendingOpen);
    const target = idx >= 0 ? entries[idx] : undefined;
    if (target && target.kind === "composed") {
      setEditingIdx(idx);
      startEditEntry(target, day);
    } else {
      setEditingIdx(null);
    }
  }, [pendingOpen, entries, startEditEntry, day]);

  // Multi-add: picking N individual exercises stages them as N members of
  // ONE form block (composeBlocks splits them into N separate entries only
  // at commit). Left staged, the list showed a single combined editor — the
  // "adds N, shows 1" bug. So after a >1 individual add, commit immediately
  // → N separate rows appear at once, none expanded. (A single add keeps
  // staging so its editor opens; a superset stays one grouped entry.)
  const [pendingMultiAdd, setPendingMultiAdd] = useState(false);
  useEffect(() => {
    if (!pendingMultiAdd) return;
    if (form.editing === null && form.members.length > 1) {
      setPendingMultiAdd(false);
      commit();
    } else if (form.members.length === 0) {
      // Form cleared before the effect ran — disarm so the flag can't later
      // fire on an unrelated single-exercise compose.
      setPendingMultiAdd(false);
    }
  }, [pendingMultiAdd, form.editing, form.members.length, commit]);

  const openEntry = (idx: number) => {
    if (editingIdx === idx && form.editing !== null) {
      collapse();
      return;
    }
    const target = entries[idx];
    if (!target || target.kind !== "composed") return;
    const identity = entryIdentity(target);
    flush();
    setEditingIdx(idx);
    setPendingOpen(identity);
  };
  const addExercise = () => {
    flush();
    clearRamp();
    setEditingIdx(null);
    setPicker("add");
  };
  const removeCurrent = () => {
    if (form.editing !== null && editingIdx !== null) {
      const e = entries[editingIdx];
      if (e && e.kind === "composed") removeEntry(e, day);
      else resetForm();
    } else {
      resetForm();
    }
    setEditingIdx(null);
  };

  // Save reads the draft, so the open editor must commit first. commit()
  // empties the form asynchronously; this effect fires the save once it has.
  // If commit() was blocked by a conflict (form stays populated, formConflict
  // set) the pending save is cancelled instead of misfiring on a later edit.
  //
  // The open form must be COMMITTABLE (≥1 day + ≥1 week) before we arm the
  // deferred save: an un-committable edit (every D- or W-chip deselected) would
  // make commit() a silent no-op, the form would never clear, and pendingSave
  // would hang — swallowing the whole Save, including edits already committed to
  // the draft. When it can't commit, persist the draft as-is and leave the
  // stray edit staged in the form for the user to fix.
  const pendingSave = useRef(false);
  const handleSave = () => {
    if (hasForm && canCommit) {
      commit();
      clearRamp();
      pendingSave.current = true;
      setEditingIdx(null);
    } else {
      void save();
    }
  };
  useEffect(() => {
    if (!pendingSave.current) return;
    if (form.editing === null && form.members.length === 0) {
      pendingSave.current = false;
      void save();
    } else if (formConflict) {
      pendingSave.current = false;
    }
  }, [form.editing, form.members.length, formConflict, save]);

  // Picker commits. Replace mode swaps one member (first pick wins); add
  // mode appends, splitting >1 individual exercises into separate rows.
  const onAddMany = (slugs: string[]) => {
    if (picker !== null && picker !== "add") {
      if (slugs[0]) replaceMember(picker.replace, slugs[0]);
      return;
    }
    addExercises(slugs, addDays);
    if (slugs.length > 1) setPendingMultiAdd(true);
  };
  const onCreateSuperset = (addSlugs: string[]) => {
    // Replace-mode picker swaps one member — never compose a superset.
    if (picker !== null && picker !== "add") return;
    addSuperset(addSlugs, addDays);
  };

  // ⧉ Clone the open block as a NEW compose (the progression gesture: clone
  // a block, retarget its days/weeks, tweak the loads, ＋ADD). It detaches
  // to a fresh block → editingIdx is cleared so it shows as the new compose.
  const cloneCurrent = () => {
    if (!form.editing) return;
    startClone(form.editing);
    setEditingIdx(null);
  };

  // PE-22 F — a cell of the week x day matrix, for the exercise open right now.
  // The open entry is the LIVE one (entries re-derive off the draft), so this
  // never reads the block the form is holding — which after a toggle may not
  // exist any more.
  const openBlocks = (): ComposedBlock[] => {
    if (editingIdx === null) return [];
    const target = entries[editingIdx];
    return target && target.kind === "composed" ? target.blocks : [];
  };

  // Through the shared predicate, not a second copy of it: the toggle already
  // answers this question in programMatrixEdits, and two copies of "is it
  // placed here" that can drift is the worst debt §CONSOLIDAMENTO names.
  const isPlacedAt = (dayIdx: number, week: number): boolean =>
    openBlocks().some((b) => isPlaced(b, dayIdx, week));

  // Three steps, and the order is the whole point:
  //  1. flush — commit() recomposes placement from form.days/form.weeks, so
  //     running it AFTER the write would rebuild the old placement over the
  //     new one and silently undo the tap;
  //  2. write — the pure toggle on the draft;
  //  3. reopen — a non-rectangular placement splits into two blocks
  //     (ComposedBlock.weeks is block-wide), so form.editing must be reloaded
  //     from the re-derived entries. Same pendingOpen the day switch uses.
  const togglePlacementAt = (dayIdx: number, week: number) => {
    if (editingIdx === null) return;
    // The block that owns this week, else the entry's first — its members are
    // what a brand-new cell places.
    const open = entries[editingIdx];
    if (!open || open.kind !== "composed") return;
    const blocks = open.blocks;
    const block = blocks.find((b) => b.weeks.includes(week)) ?? blocks[0];
    if (!block) return;
    const identity = entryIdentity(open);
    flush();
    togglePlacement(block, dayIdx, week);
    setPendingOpen(identity);
  };

  return {
    picker,
    setPicker,
    isPlacedAt,
    togglePlacementAt,
    editingIdx,
    editing: form.editing !== null,
    hasForm,
    composingNew,
    openEntry,
    addExercise,
    removeCurrent,
    handleSave,
    collapse,
    cloneCurrent,
    onAddMany,
    onCreateSuperset,
  };
}
